/**
 * Editor de conexiones de salidas del splitter
 * Permite conectar salidas a cables de distribución o puertos NAP
 * @module components/forms/SplitterConnectionEditor
 */

import { useState, useEffect, useMemo } from 'react'
import { X, Cable, Plug, Circle, Check, AlertCircle } from 'lucide-react'
import { listFibrasParaPicker } from '../../utils/cableFibrasPicker'
import { cableLabelForSelect, cableLabelShort } from '@/utils/cableDisplayLabel'

// Colores TIA-598
const COLORES_TIA598 = [
  { nombre: 'Azul', codigo: 'azul', hex: '#0066CC' },
  { nombre: 'Naranja', codigo: 'naranja', hex: '#FF6600' },
  { nombre: 'Verde', codigo: 'verde', hex: '#00AA00' },
  { nombre: 'Marrón', codigo: 'marron', hex: '#8B4513' },
  { nombre: 'Gris', codigo: 'gris', hex: '#808080' },
  { nombre: 'Blanco', codigo: 'blanco', hex: '#FFFFFF' },
  { nombre: 'Rojo', codigo: 'rojo', hex: '#FF0000' },
  { nombre: 'Negro', codigo: 'negro', hex: '#000000' },
  { nombre: 'Amarillo', codigo: 'amarillo', hex: '#FFCC00' },
  { nombre: 'Violeta', codigo: 'violeta', hex: '#9900CC' },
  { nombre: 'Rosa', codigo: 'rosa', hex: '#FF66CC' },
  { nombre: 'Turquesa', codigo: 'turquesa', hex: '#00CCCC' },
]

const getColorHex = (color: string) => COLORES_TIA598.find(c => c.codigo === color)?.hex || '#CCCCCC'

export interface SplitterSalida {
  puerto: number
  colorPigtail: string
  estado: 'libre' | 'conectado' | 'reservado'
  destino?: {
    tipo: 'cable' | 'puerto_nap' | 'splitter'
    cableId?: string
    cableNombre?: string
    bufferNum?: number
    fibraNum?: number
    fibraColor?: string
    fibraId?: string
    puertoNAP?: number
    splitterDestinoId?: string
  }
}

interface CableConFibras {
  id: string
  codigo?: string
  nombre?: string
  tipo: string
  numeroFibras: number
  tieneBuffers: boolean
  buffers?: Array<{
    numero: number
    color: string
    fibras: Array<{
      numero: number
      color: string
      estado: string
      fibraId: string
    }>
  }>
  fibras?: Array<{
    numero: number
    color: string
    estado: string
    fibraId: string
  }>
}

interface SplitterDisponible {
  id: string
  ratio: string
  nombre?: string
  entradaConfigurada?: boolean
}

interface SplitterConnectionEditorProps {
  isOpen: boolean
  onClose: () => void
  splitterId: string
  splitterRatio: string
  tipoSplitter?: 'balanceado' | 'desbalanceado'
  salidas: SplitterSalida[]
  cablesDisponibles: CableConFibras[]
  puertosNAP: Array<{ numero: number; estado: string }>
  onSave: (salidas: SplitterSalida[]) => void
  isNAP2doNivel?: boolean
  splittersDisponibles?: SplitterDisponible[]
}

export function SplitterConnectionEditor({
  isOpen,
  onClose,
  splitterId,
  splitterRatio,
  tipoSplitter = 'balanceado',
  salidas: salidasIniciales,
  cablesDisponibles,
  puertosNAP,
  onSave,
  isNAP2doNivel = false,
  splittersDisponibles = [],
}: SplitterConnectionEditorProps) {
  // Un splitter desbalanceado (tap) siempre tiene 2 salidas físicas: cascada y local.
  // `splitterRatio` (1:2..1:64) solo aplica a splitters balanceados.
  const numSalidas = tipoSplitter === 'desbalanceado' ? 2 : parseInt(splitterRatio.split(':')[1])
  
  // Filtrar splitters disponibles (excluir el actual y los que ya tienen entrada configurada desde este splitter)
  const splittersParaCascada = splittersDisponibles.filter(s => 
    s.id !== splitterId && !s.entradaConfigurada
  )
  
  const salidasSyncKey = useMemo(
    () => JSON.stringify(salidasIniciales),
    [salidasIniciales]
  )

  const buildSalidasFromProps = (): SplitterSalida[] =>
    Array.from({ length: numSalidas }, (_, i) => {
      const existente = salidasIniciales.find((s) => s.puerto === i + 1)
      return (
        existente || {
          puerto: i + 1,
          colorPigtail: COLORES_TIA598[i % 12].codigo,
          estado: 'libre' as const,
        }
      )
    })

  const [salidas, setSalidas] = useState<SplitterSalida[]>(() => buildSalidasFromProps())

  const [selectedSalida, setSelectedSalida] = useState<number | null>(null)

  useEffect(() => {
    if (!isOpen) return
    setSalidas(buildSalidasFromProps())
    setSelectedSalida(null)
    // numSalidas y salidasSyncKey reflejan ratio y contenido de salidasIniciales
    // eslint-disable-next-line react-hooks/exhaustive-deps -- buildSalidasFromProps cierra sobre props actuales
  }, [isOpen, splitterId, splitterRatio, numSalidas, salidasSyncKey])

  if (!isOpen) return null

  const updateSalida = (puerto: number, updates: Partial<SplitterSalida>) => {
    setSalidas(prev => prev.map(s => 
      s.puerto === puerto ? { ...s, ...updates } : s
    ))
  }

  const conectarACable = (puerto: number, cableId: string, fibraId: string, fibraInfo: {
    bufferNum?: number
    fibraNum: number
    fibraColor: string
    cableNombre?: string
  }) => {
    updateSalida(puerto, {
      estado: 'conectado',
      destino: {
        tipo: 'cable',
        cableId,
        cableNombre: fibraInfo.cableNombre,
        bufferNum: fibraInfo.bufferNum,
        fibraNum: fibraInfo.fibraNum,
        fibraColor: fibraInfo.fibraColor,
        fibraId,
      }
    })
    setSelectedSalida(null)
  }

  const conectarAPuertoNAP = (puerto: number, puertoNAP: number) => {
    updateSalida(puerto, {
      estado: 'conectado',
      destino: {
        tipo: 'puerto_nap',
        puertoNAP,
      }
    })
    setSelectedSalida(null)
  }

  const conectarASplitter = (puerto: number, splitterDestinoId: string, splitterNombre?: string) => {
    updateSalida(puerto, {
      estado: 'conectado',
      destino: {
        tipo: 'splitter',
        splitterDestinoId,
        cableNombre: splitterNombre || `Splitter ${splitterDestinoId.slice(-6)}`,
      }
    })
    setSelectedSalida(null)
  }

  const liberarSalida = (puerto: number) => {
    updateSalida(puerto, {
      estado: 'libre',
      destino: undefined,
    })
  }

  const marcarReservado = (puerto: number) => {
    updateSalida(puerto, {
      estado: 'reservado',
      destino: undefined,
    })
  }

  const handleSave = () => {
    onSave(salidas)
    onClose()
  }

  const getFibrasDisponibles = (cable: CableConFibras) => listFibrasParaPicker(cable)

  const salidasConectadas = salidas.filter(s => s.estado === 'conectado').length
  const salidasLibres = salidas.filter(s => s.estado === 'libre').length
  const salidasReservadas = salidas.filter(s => s.estado === 'reservado').length

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]">
      <div className="bg-white dark:bg-dark-800 rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-purple-600 text-white p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Plug className="w-6 h-6" />
            <div>
              <h2 className="text-lg font-semibold">Configurar Salidas del Splitter</h2>
              <p className="text-sm text-purple-200">Splitter {splitterRatio} - {numSalidas} salidas</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-purple-700 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Resumen */}
        <div className="bg-purple-50 dark:bg-purple-900/20 p-3 flex items-center justify-around text-sm border-b">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500" />
            <span>{salidasLibres} libres</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-blue-500" />
            <span>{salidasConectadas} conectadas</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-yellow-500" />
            <span>{salidasReservadas} reservadas</span>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="grid grid-cols-2 gap-4">
            {/* Lista de Salidas */}
            <div>
              <h3 className="font-medium mb-3 flex items-center gap-2">
                <Cable className="w-4 h-4" />
                Salidas del Splitter
              </h3>
              <div className="space-y-2">
                {salidas.map((salida) => {
                  const isSelected = selectedSalida === salida.puerto
                  const estadoClass = salida.estado === 'conectado'
                    ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                    : salida.estado === 'reservado'
                      ? 'border-yellow-400 bg-yellow-50 dark:bg-yellow-900/20'
                      : 'border-gray-300 bg-white dark:bg-dark-700'

                  return (
                    <div
                      key={salida.puerto}
                      className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${estadoClass} ${
                        isSelected ? 'ring-2 ring-purple-500' : ''
                      }`}
                      onClick={() => setSelectedSalida(isSelected ? null : salida.puerto)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Circle
                            className="w-4 h-4"
                            fill={getColorHex(salida.colorPigtail)}
                            stroke={getColorHex(salida.colorPigtail)}
                          />
                          <span className="font-medium">Salida {salida.puerto}</span>
                          <span className="text-xs text-gray-500">({salida.colorPigtail})</span>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          salida.estado === 'conectado' ? 'bg-blue-200 text-blue-800' :
                          salida.estado === 'reservado' ? 'bg-yellow-200 text-yellow-800' :
                          'bg-gray-200 text-gray-600'
                        }`}>
                          {salida.estado}
                        </span>
                      </div>

                      {salida.destino && (
                        <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                          {salida.destino.tipo === 'cable' ? (
                            <div className="flex items-center gap-1">
                              <Cable className="w-3 h-3" />
                              <span>
                                {salida.destino.cableNombre} → 
                                {salida.destino.bufferNum ? ` B${salida.destino.bufferNum}` : ''} 
                                H{salida.destino.fibraNum} ({salida.destino.fibraColor})
                              </span>
                            </div>
                          ) : salida.destino.tipo === 'splitter' ? (
                            <div className="flex items-center gap-1 text-purple-600 dark:text-purple-400">
                              <AlertCircle className="w-3 h-3" />
                              <span>Cascada → {salida.destino.cableNombre || 'Splitter'}</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1">
                              <Plug className="w-3 h-3" />
                              <span>Puerto NAP #{salida.destino.puertoNAP}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {isSelected && salida.estado !== 'libre' && (
                        <div className="mt-2 flex gap-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); liberarSalida(salida.puerto) }}
                            className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200"
                          >
                            Liberar
                          </button>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Panel de Conexión */}
            <div>
              {selectedSalida ? (
                <div>
                  <h3 className="font-medium mb-3">
                    Conectar Salida {selectedSalida}
                  </h3>

                  {/* Acciones rápidas */}
                  <div className="flex gap-2 mb-4">
                    <button
                      onClick={() => marcarReservado(selectedSalida)}
                      className="text-sm px-3 py-1 bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200"
                    >
                      Marcar Reservado
                    </button>
                    <button
                      onClick={() => liberarSalida(selectedSalida)}
                      className="text-sm px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                    >
                      Dejar Libre
                    </button>
                  </div>

                  {/* Opción: Conectar a Puerto NAP (para NAP 2do nivel) */}
                  {isNAP2doNivel && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                        <Plug className="w-4 h-4 text-purple-500" />
                        Conectar a Puerto NAP (cliente)
                      </h4>
                      <div className="grid grid-cols-4 gap-2">
                        {puertosNAP.map(puerto => {
                          const yaConectado = salidas.some(
                            s => s.destino?.tipo === 'puerto_nap' && s.destino.puertoNAP === puerto.numero
                          )
                          const ocupado = puerto.estado === 'ocupado' || yaConectado

                          return (
                            <button
                              key={puerto.numero}
                              onClick={() => !ocupado && conectarAPuertoNAP(selectedSalida, puerto.numero)}
                              disabled={ocupado}
                              className={`p-2 border rounded text-sm ${
                                ocupado
                                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                  : 'hover:border-purple-400 hover:bg-purple-50'
                              }`}
                            >
                              P{puerto.numero}
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  )}

                  {/* Opción: Conectar a Cable (para NAP 1er nivel) */}
                  <div>
                    <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                      <Cable className="w-4 h-4 text-blue-500" />
                      Conectar a Cable de Distribución
                    </h4>
                    
                    {cablesDisponibles.length === 0 ? (
                      <div className="text-sm text-gray-500 bg-gray-100 dark:bg-dark-700 p-3 rounded flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        No hay cables de salida disponibles
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {cablesDisponibles.map(cable => {
                          const fibras = getFibrasDisponibles(cable)
                          const fibrasLibres = fibras.filter(f => f.estado === 'libre')

                          return (
                            <div key={cable.id} className="border rounded-lg p-2 dark:border-dark-600">
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-medium text-sm leading-tight">
                                  {cableLabelForSelect(cable)}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {fibrasLibres.length}/{fibras.length} libres
                                </span>
                              </div>
                              
                              <div className="grid grid-cols-6 gap-1">
                                {fibras.slice(0, 12).map(fibra => {
                                  const yaUsada = salidas.some(
                                    s => s.destino?.fibraId === fibra.fibraId
                                  )
                                  const disponible = fibra.estado === 'libre' && !yaUsada

                                  return (
                                    <button
                                      key={fibra.fibraId}
                                      onClick={() => disponible && conectarACable(
                                        selectedSalida,
                                        cable.id,
                                        fibra.fibraId,
                                        {
                                          bufferNum: fibra.bufferNum,
                                          fibraNum: fibra.fibraNum,
                                          fibraColor: fibra.fibraColor,
                                          cableNombre: cableLabelShort(cable),
                                        }
                                      )}
                                      disabled={!disponible}
                                      className={`p-1 border rounded text-xs ${
                                        disponible
                                          ? 'hover:border-blue-400 hover:bg-blue-50 cursor-pointer'
                                          : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                      }`}
                                      title={`${fibra.bufferNum ? `B${fibra.bufferNum}-` : ''}H${fibra.fibraNum} (${fibra.fibraColor}) - ${fibra.estado}`}
                                    >
                                      <Circle
                                        className="w-3 h-3 mx-auto"
                                        fill={getColorHex(fibra.fibraColor)}
                                        stroke={disponible ? getColorHex(fibra.fibraColor) : '#999'}
                                      />
                                      <div className="text-[10px] mt-0.5">
                                        {fibra.bufferNum ? `${fibra.bufferNum}-` : ''}
                                        {fibra.fibraNum}
                                      </div>
                                    </button>
                                  )
                                })}
                              </div>
                              
                              {fibras.length > 12 && (
                                <div className="text-xs text-gray-500 mt-1 text-center">
                                  +{fibras.length - 12} fibras más...
                                </div>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>

                  {/* Opción: Conectar a otro Splitter (cascada) */}
                  {splittersParaCascada.length > 0 && (
                    <div className="mt-4 pt-4 border-t">
                      <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-purple-500" />
                        Conectar a otro Splitter (Cascada)
                      </h4>
                      <p className="text-xs text-gray-500 mb-2">
                        Conecta esta salida a la entrada de otro splitter para crear una cascada
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        {splittersParaCascada.map((spl, idx) => {
                          const yaConectado = salidas.some(
                            s => s.destino?.tipo === 'splitter' && s.destino.splitterDestinoId === spl.id
                          )
                          
                          return (
                            <button
                              key={spl.id}
                              onClick={() => !yaConectado && conectarASplitter(
                                selectedSalida,
                                spl.id,
                                spl.nombre || `Splitter ${idx + 1} (${spl.ratio})`
                              )}
                              disabled={yaConectado || spl.entradaConfigurada}
                              className={`p-2 border-2 rounded text-sm flex items-center gap-2 ${
                                yaConectado || spl.entradaConfigurada
                                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200'
                                  : 'hover:border-purple-400 hover:bg-purple-50 border-purple-200'
                              }`}
                            >
                              <AlertCircle className="w-4 h-4 text-purple-500" />
                              <div className="text-left">
                                <div className="font-medium">{spl.nombre || `Splitter ${idx + 1}`}</div>
                                <div className="text-xs text-gray-500">{spl.ratio}</div>
                              </div>
                              {yaConectado && (
                                <span className="ml-auto text-xs bg-purple-100 text-purple-700 px-1 rounded">
                                  Conectado
                                </span>
                              )}
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="h-full flex items-center justify-center text-gray-500">
                  <div className="text-center">
                    <Plug className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p>Selecciona una salida para configurar</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
          >
            <Check className="w-4 h-4" />
            Guardar Conexiones
          </button>
        </div>
      </div>
    </div>
  )
}
